/**
 * TikZ-Grafiken über die Kroki-API rendern.
 *
 * Der Markdown-Renderer erzeugt für `::: tikz … :::` bzw. ```` ```tikz ````
 * nur eine Hülle mit der Quelle im `data-tikz`-Attribut (siehe ./index.ts).
 * Das eigentliche SVG holt diese Datei per POST von kroki.io.
 *
 * Zwischenspeicher: Der Quelltext wird gehasht; identische Quellen werden nie
 * erneut angefragt und nicht erneut gespeichert. Nur eine *Änderung* kostet
 * einen Request und einen Schreibvorgang.
 */

import { svgCached, svgLoad, svgStore } from "./svgCache";
import { dropStale, staleOf } from "./reuse";

const KROKI_URL = "https://kroki.io";
const LS_ITEM = "md.tikz.";
const LS_INDEX = "md.tikz.index";
const MAX_ENTRIES = 40;
const MAX_ITEM_BYTES = 300_000;

const memory = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

/** Kurzer, stabiler Hash (FNV-1a, Base36) – reicht als Cache-Schlüssel. */
export function hashSource(src: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36) + src.length.toString(36);
}

function readIndex(): string[] {
  try {
    const raw = localStorage.getItem(LS_INDEX);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function cacheGet(key: string): string | null {
  const hit = memory.get(key);
  if (hit) return hit;
  try {
    const stored = localStorage.getItem(LS_ITEM + key);
    if (stored) {
      memory.set(key, stored);
      return stored;
    }
  } catch {
    /* localStorage nicht verfügbar (Privatmodus) – nur RAM-Cache */
  }
  return null;
}

/** Schreibt nur bei neuem Schlüssel; hält den Vorrat klein (LRU-artig). */
function cachePut(key: string, svg: string): void {
  memory.set(key, svg);
  if (svg.length > MAX_ITEM_BYTES) return;
  try {
    if (localStorage.getItem(LS_ITEM + key) != null) return; // unverändert
    const index = readIndex().filter((k) => k !== key);
    index.push(key);
    while (index.length > MAX_ENTRIES) {
      const drop = index.shift();
      if (drop) localStorage.removeItem(LS_ITEM + drop);
    }
    localStorage.setItem(LS_ITEM + key, svg);
    localStorage.setItem(LS_INDEX, JSON.stringify(index));
  } catch {
    /* Speicher voll o. Ä. – RAM-Cache genügt für die Sitzung */
  }
}

/**
 * Macht aus einem TikZ-Schnipsel ein vollständiges LaTeX-Dokument. Kroki
 * erwartet ein kompilierbares Dokument; im Editor soll aber ein einzelnes
 * `\draw …` genügen.
 */
export function toLatexDocument(src: string): string {
  const body = src.trim();
  if (/\\begin\{document\}/.test(body)) {
    return /\\documentclass/.test(body)
      ? body
      : `\\documentclass[tikz,border=6pt]{standalone}\n${body}`;
  }
  const picture = /\\begin\{tikzpicture\}/.test(body)
    ? body
    : `\\begin{tikzpicture}\n${body}\n\\end{tikzpicture}`;
  return (
    `\\documentclass[tikz,border=6pt]{standalone}\n` +
    `\\usepackage{tikz}\n` +
    `\\begin{document}\n${picture}\n\\end{document}`
  );
}

async function fetchSvg(source: string, type: string): Promise<string> {
  const res = await fetch(`${KROKI_URL}/${type}/svg`, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: type === "tikz" ? toLatexDocument(source) : source,
  });
  const text = await res.text();
  if (res.status === 413) throw new Error("Quelltext zu groß für Kroki.");
  if (!res.ok) throw new Error(shortError(text) || `Kroki-Fehler ${res.status}`);
  if (!/<svg[\s>]/i.test(text)) throw new Error("Kroki lieferte kein SVG zurück.");

  // XML-Prolog/DOCTYPE entfernen – in HTML eingehängt wären das Fremdkörper.
  const svg = text.replace(/^\s*(?:<\?xml[\s\S]*?\?>|<!DOCTYPE[\s\S]*?>)\s*/gi, "").trim();

  // LaTeX „repariert" Syntaxfehler still und liefert dann eine winzige, leere
  // Grafik. Das ist für den Nutzer wertlos – lieber als Fehler melden.
  const size = /width='([\d.]+)pt'\s+height='([\d.]+)pt'/.exec(svg);
  if (size && Number(size[1]) < 1 && Number(size[2]) < 1) {
    throw new Error("Leere Grafik – bitte die TikZ-Syntax prüfen.");
  }
  return svg;
}

/** Häufige LaTeX-Fehler auf Deutsch – lesbarer als die Original-Meldung. */
const KNOWN_ERRORS: [RegExp, string][] = [
  [/Undefined control sequence/i, "Unbekannter LaTeX-Befehl (Tippfehler oder fehlendes Paket?)."],
  [/Misplaced alignment tab/i, "Falsch platziertes „&“ außerhalb einer Tabelle."],
  [/Missing \$ inserted/i, "Mathe-Modus fehlt – `$…$` um Formelzeichen setzen."],
  [/Extra \}, or forgotten/i, "Geschweifte Klammer zu viel oder eine fehlt."],
  [/File.*not found/i, "Referenzierte Datei/Bibliothek wurde nicht gefunden."],
  [/Environment .* undefined/i, "Unbekannte TikZ-Umgebung."],
];

/** Aus der oft langen LaTeX-Fehlerausgabe die aussagekräftige Zeile ziehen. */
function shortError(text: string): string {
  const line =
    text
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l.startsWith("!")) ?? "";
  const known = KNOWN_ERRORS.find(([re]) => re.test(line))?.[1];
  const clean = line.replace(/^!\s*/, "");
  const combined = known ? `${known} (${clean})` : clean;
  return combined.slice(0, 200) || "Konnte nicht kompiliert werden.";
}

async function svgFor(source: string, key: string, type: string): Promise<string> {
  const cached = cacheGet(key) ?? (await svgLoad(`tikz:${key}`));
  if (cached) {
    cachePut(key, cached);
    return cached;
  }
  const running = pending.get(key);
  if (running) return running;

  const task = fetchSvg(source, type)
    .then((svg) => {
      cachePut(key, svg);
      // Dauerhaft ablegen, damit ein Neuladen der Seite nichts neu holt.
      void svgStore(`tikz:${key}`, svg);
      return svg;
    })
    .finally(() => pending.delete(key));
  pending.set(key, task);
  return task;
}

/**
 * Rendert alle TikZ-Hüllen unterhalb von `root`. Mehrfach aufrufbar: bereits
 * gerenderte Knoten mit unveränderter Quelle werden übersprungen.
 */
export async function hydrateTikz(root: HTMLElement): Promise<void> {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(".md-tikz[data-tikz]"));
  await Promise.all(
    nodes.map(async (node) => {
      const source = decodeURIComponent(node.dataset.tikz || "");
      const type = node.dataset.krokiType || "tikz";
      const key = type === "tikz" ? hashSource(source) : hashSource(`${type}\n${source}`);
      if (node.dataset.tikzDone === key) return;
      if (!source.trim()) return;

      // Auch die dauerhafte Datenbank (IndexedDB) prüfen, BEVOR der Ladezustand
      // aufblitzt – sonst zeigt jeder Reload kurz "wird geladen", obwohl das
      // Ergebnis längst gespeichert ist (der Speicher-Cache ist ja gerade nach
      // einem Neuladen leer, nur die Datenbank hat den alten Stand).
      const cached = cacheGet(key) ?? svgCached(`tikz:${key}`) ?? (await svgLoad(`tikz:${key}`));
      if (cached) {
        cachePut(key, cached);
        paint(node, cached, key);
        return;
      }
      node.classList.add("is-loading");
      try {
        paint(node, await svgFor(source, key, type), key);
      } catch (err) {
        const stale = staleOf(node);
        node.classList.remove("is-loading", "md-pending");
        node.dataset.tikzDone = "";
        node.innerHTML =
          `<div class="md-diagram-error"><span class="md-diagram-error-tag">${type === "dbml" ? "DBML" : "TikZ"}</span>` +
          `<span class="md-diagram-error-msg"></span></div>`;
        const msg = node.querySelector(".md-diagram-error-msg");
        if (msg) msg.textContent = (err as Error).message || "Konnte nicht gerendert werden.";
        if (stale) {
          node.classList.add("md-diagram-hold");
          node.prepend(stale);
        }
      }
    })
  );
}

function paint(node: HTMLElement, svg: string, key: string): void {
  dropStale(node);
  node.style.minHeight = "";
  node.classList.remove("md-diagram-hold");
  node.classList.remove("is-loading");
  node.dataset.tikzDone = key;
  node.innerHTML = `<div class="md-tikz-svg">${svg}</div>`;
}
